import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '../api/client.js';
import { Badge, Card, Empty, Failure, Loading, Notice, Stat } from '../components/ui.js';
import { FAULT_LABELS, READINESS } from '../lib/format.js';

interface Overview {
  schools: number;
  devices_total: number;
  devices_online: number;
  devices_offline: number;
  devices_ready: number;
  devices_degraded: number;
  devices_not_ready: number;
  devices_behind_schedule: number;
  active_incidents: number;
  pending_deployments: number;
  failed_audio: number;
  stale_outbox: number;
  fault_counts: Record<string, number>;
  attention: Array<{
    device_id: string;
    device_name: string;
    school_name: string;
    readiness: string;
    online: boolean;
    reasons: string[];
  }>;
  note: string;
}

export function OverviewPage(): JSX.Element {
  const query = useQuery({
    queryKey: ['overview'],
    queryFn: () => get<Overview>('/overview'),
    refetchInterval: 15_000,
  });

  if (query.isLoading) return <Loading />;
  if (query.error) return <Failure error={query.error} onRetry={() => void query.refetch()} />;
  const data = query.data!;

  return (
    <>
      {data.active_incidents > 0 ? (
        <Notice tone="danger">
          يوجد <strong className="num">{data.active_incidents}</strong> حادثة طوارئ نشطة الآن.{' '}
          <Link to="/emergency">افتح شاشة الطوارئ</Link>
        </Notice>
      ) : null}

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Stat label="المدارس" value={<span className="num">{data.schools}</span>} />
        <Stat label="الأجهزة المفعّلة" value={<span className="num">{data.devices_total}</span>} />
        <Stat
          label="جاهزة"
          value={<span className="num">{data.devices_ready}</span>}
          tone="ok"
          hint="جدول صالح وصوت متاح ووقت موثوق"
        />
        <Stat
          label="غير جاهزة"
          value={<span className="num">{data.devices_not_ready}</span>}
          tone={data.devices_not_ready > 0 ? 'danger' : undefined}
        />
      </div>

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Stat
          label="متصلة بالمراقبة"
          value={<span className="num">{data.devices_online}</span>}
          hint="آخر اتصال خلال 90 ثانية"
        />
        <Stat
          label="منقطعة عن المراقبة"
          value={<span className="num">{data.devices_offline}</span>}
          tone={data.devices_offline > 0 ? 'warn' : undefined}
        />
        <Stat
          label="لم تطبّق الجدول"
          value={<span className="num">{data.devices_behind_schedule}</span>}
          tone={data.devices_behind_schedule > 0 ? 'warn' : undefined}
        />
        <Stat
          label="ملفات صوت فشل تحويلها"
          value={<span className="num">{data.failed_audio}</span>}
          tone={data.failed_audio > 0 ? 'danger' : undefined}
        />
      </div>

      {/* §7 and §13: an offline device is a monitoring gap, not proof the bell stopped. */}
      <Notice tone="info">{data.note}</Notice>

      {data.stale_outbox > 0 ? (
        <Notice tone="warn">
          هناك <span className="num">{data.stale_outbox}</span> رسالة متأخرة في صندوق الإرسال؛ تحقق
          من اتصال الوسيط وعامل المهام.
        </Notice>
      ) : null}

      <Card title="أجهزة تحتاج انتباهًا" subtitle={`${data.attention.length} جهازًا`}>
        {data.attention.length === 0 ? (
          <Empty title="كل الأجهزة جاهزة" hint="لا توجد أعطال أو نسخ جدول متأخرة." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الجهاز</th>
                  <th>المدرسة</th>
                  <th>الجاهزية</th>
                  <th>الاتصال</th>
                  <th>السبب</th>
                </tr>
              </thead>
              <tbody>
                {data.attention.map((d) => {
                  const r = READINESS[d.readiness] ?? READINESS.unknown!;
                  return (
                    <tr key={d.device_id}>
                      <td>
                        <Link to={`/devices/${d.device_id}`}>{d.device_name}</Link>
                      </td>
                      <td>{d.school_name}</td>
                      <td>
                        <Badge tone={r.tone} icon={r.icon}>
                          {r.label}
                        </Badge>
                      </td>
                      <td>
                        {d.online ? (
                          <Badge tone="ok" icon="✔">
                            متصل
                          </Badge>
                        ) : (
                          <Badge tone="warn" icon="▲">
                            منقطع
                          </Badge>
                        )}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                          {d.reasons.map((reason, i) => (
                            <li key={i}>{reason}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="ملخص الأعطال المبلّغة">
        {Object.keys(data.fault_counts).length === 0 ? (
          <Empty title="لا توجد أعطال مبلّغة" />
        ) : (
          <div className="pill-list">
            {Object.entries(data.fault_counts).map(([fault, count]) => (
              <Badge key={fault} tone="danger" icon="✖">
                {FAULT_LABELS[fault] ?? fault}: <span className="num">{count}</span>
              </Badge>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
